import React, { useState, useEffect } from 'react';
import {
  Download, FileText, Presentation, Image as ImageIcon, ExternalLink,
  Briefcase, Store, Mic2, Share2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DOWNLOADS } from '@/constants/testIds';
import { AAWTA } from '@/lib/aawta';
import { api, fileUrl } from '@/lib/api';

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

const ICON_MAP = {
  FileText, Presentation, ImageIcon, Briefcase, Store, Mic2, Share2, Download,
};

const CARDS = [
  {
    testId: DOWNLOADS.flyer,
    icon: ImageIcon,
    tab: 'Event Flyer',
    title: 'Official Summit Flyer',
    desc: 'Official Summit flyer — perfect for sharing with your network.',
    label: 'View Flyer',
    url: (a) => a.flyerUrl,
    kind: 'PNG',
  },
  {
    testId: DOWNLOADS.agenda,
    icon: FileText,
    tab: 'Full Agenda',
    title: '2026 Workforce Summit Agenda',
    desc: 'View the updated September 17 program, including keynotes, panels, partner spotlights, breakout sessions, roundtables, exhibitor networking, and the VIP reception.',
    label: 'Download PDF',
    url: (a) => a.agendaPdfUrl,
    kind: 'PDF',
  },
  {
    testId: 'downloads-corporate-sponsorship',
    icon: Briefcase,
    tab: 'Corporate Sponsorship Guide',
    title: '2026 Sponsorship & Investment Prospectus — Full Version',
    desc: "Explore AAWTA's complete 2026 sponsorship and investment opportunities, partnership benefits, brand visibility, workforce impact, exhibitor opportunities, and ways organizations can support the Building the Future Workforce initiative.",
    label: 'Download PDF',
    url: (a) => a.corporateSponsorshipGuideUrl,
    kind: 'PDF',
  },
  {
    testId: 'downloads-exhibitor-guide',
    icon: Store,
    tab: 'Exhibitor Guide',
    title: '2026 Exhibitor Guide & Welcome Packet',
    desc: 'Everything exhibitors need to prepare for Workforce Summit 2026, including event logistics, booth setup, arrival times, participation requirements, important deadlines, promotional opportunities, marketing resources, and event-day expectations.',
    more: 'This guide is intended to help exhibitors prepare for a successful Summit experience. The current Exhibitor Guide confirms the Summit date, venue, exhibitor logistics, marketing resources, and other preparation information.',
    label: 'Download PDF',
    url: (a) => a.exhibitorGuideUrl,
    kind: 'PDF',
  },
  {
    testId: 'downloads-speaker-guide',
    icon: Mic2,
    tab: 'Speaker, Panelist & Session Leader Guide',
    title: '2026 Speaker, Panelist, Roundtable Facilitator & Breakout Session Guide',
    desc: 'Your official preparation guide for participating as a speaker, panelist, moderator, roundtable facilitator, or breakout session leader at Workforce Summit 2026.',
    more: 'This guide provides important event-day instructions, arrival expectations, session preparation guidance, marketing requirements, participant engagement recommendations, media expectations, and resources to help speakers and session leaders deliver an organized, engaging, and impactful Summit experience. Please review the guide before your scheduled session and share applicable information with members of your participating team.',
    label: 'Download PDF',
    url: (a) => a.speakerGuideUrl,
    kind: 'PDF',
  },
  {
    testId: 'downloads-im-attending',
    icon: Share2,
    tab: "I’m Attending",
    title: "I’m Attending Workforce Summit 2026 — Canva Template",
    desc: "Attending Workforce Summit 2026? Make it official! Customize our Canva graphic with your photo and share that you'll be joining us for this historic workforce experience.",
    moreTitle: 'How to Use Your Graphic',
    more: `Open the Canva template, upload your photo, customize your graphic, and download your completed design. Share it on LinkedIn, Facebook, or Instagram to let your network know you'll be joining AAWTA at Building the Future Workforce Summit 2026.

Suggested Caption
I'm excited to attend AAWTA's inaugural Building the Future Workforce Summit 2026, bringing together leaders from education, industry, workforce development, government, transportation, logistics, and supply chain.

I'm looking forward to connecting, learning, and being part of the conversations helping build stronger workforce pathways and opportunities.

📅 September 17, 2026
📍 Stone Ridge Event Center | Stone Mountain, GA

Building Pathways. Creating Opportunities. Driving Change.

Suggested Hashtags
#AAWTA  #AAWTAWorkforceSummit2026  #BuildingTheFutureWorkforce  #WorkforceDevelopment  #Transportation  #Logistics  #SupplyChain  #CareerPathways  #WorkforceInnovation`,
    label: 'Customize Your Graphic',
    url: () => 'https://canva.link/2ex2fog9avaf4eh',
    kind: 'Canva',
  },
  {
    testId: DOWNLOADS.pitchDeck,
    icon: Presentation,
    tab: 'Interactive Pitch Deck',
    title: 'Interactive Pitch Deck',
    desc: 'Sponsorship tiers, benefits, and Founding Partner recognition — in an interactive Gamma deck.',
    label: 'View Deck',
    url: (a) => a.pitchDeckUrl,
    kind: 'External',
  },
  {
    testId: DOWNLOADS.logo,
    icon: ImageIcon,
    tab: 'AAWTA Logo',
    title: 'AAWTA Logo',
    desc: 'For approved co-marketing use. Please email us for high-resolution assets.',
    label: 'View Logo',
    url: (a) => a.logoUrl,
    kind: 'JPG',
  },
];

const DownloadCard = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = item.icon;
  return (
    <div
      data-testid={`download-tab-${item.tab.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
      className="group bg-aawta-surface rounded-2xl p-6 border border-aawta-border hover:bg-white hover:shadow-aawta-elevated hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-12 h-12 rounded-xl bg-aawta-navy text-white flex items-center justify-center group-hover:bg-aawta-magenta transition-colors shrink-0">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <span className="inline-flex px-2 py-0.5 rounded-full bg-white text-aawta-navy text-[10px] font-bold uppercase tracking-wider border border-aawta-border whitespace-nowrap">
          {item.kind}
        </span>
      </div>
      <div className="mt-5 overline text-aawta-magenta">{item.tab}</div>
      <h3 className="font-display font-semibold text-lg mt-1 text-aawta-navy leading-snug">
        {item.title}
      </h3>
      <p className="text-sm text-aawta-muted mt-2 leading-relaxed">{item.desc}</p>
      {item.more && (
        <>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-aawta-border max-h-[50vh] overflow-y-auto pr-1" data-testid={`${item.testId}-more`}>
              {item.moreTitle && (
                <div className="font-display font-semibold text-[13.5px] text-aawta-navy mb-2">{item.moreTitle}</div>
              )}
              <p className="text-[13.5px] text-aawta-muted leading-relaxed whitespace-pre-wrap">{item.more}</p>
            </div>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`${item.testId}-readmore`}
            className="mt-2 inline-flex self-start items-center gap-1 text-[12.5px] font-semibold text-aawta-magenta hover:text-aawta-magenta-hover"
          >
            {expanded ? (<>Read Less <ChevronUp className="h-3.5 w-3.5" /></>) : (<>Read More <ChevronDown className="h-3.5 w-3.5" /></>)}
          </button>
        </>
      )}
      <div className="mt-auto pt-5">
        <Button
          data-testid={item.testId}
          onClick={() => openExternal(item.url(AAWTA))}
          className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium w-fit"
        >
          {['external', 'canva', 'link', 'youtube'].includes(String(item.kind || '').toLowerCase()) ? (
            <>{item.label}<ExternalLink className="ml-2 h-3.5 w-3.5" /></>
          ) : (
            <><Download className="mr-2 h-3.5 w-3.5" />{item.label}</>
          )}
        </Button>
      </div>
    </div>
  );
};

export const Downloads = () => {
  const [dynamicItems, setDynamicItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/downloads');
        setDynamicItems(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    })();
  }, []);

  const dynamicCards = dynamicItems.map((it) => {
    const url = it.file_id ? fileUrl(it.file_id) : it.external_url;
    const kind = (it.kind || 'PDF').toUpperCase();
    const isExternal = ['CANVA', 'EXTERNAL', 'LINK', 'YOUTUBE'].includes(kind);
    const Icon = ICON_MAP[it.icon] || FileText;
    return {
      testId: `downloads-dynamic-${it.id}`,
      icon: Icon,
      tab: it.tab || it.title,
      title: it.title,
      desc: it.description || '',
      label: isExternal ? 'Open' : 'Download',
      url: () => {
        // Fire-and-forget click tracking
        try { api.post(`/downloads/${it.id}/click`); } catch { /* ignore */ }
        return url;
      },
      kind,
      _external: isExternal,
      _order: it.display_order ?? 0,
    };
  }).filter((c) => !!c.url());

  const allCards = [...CARDS, ...dynamicCards];

  return (
    <section id="downloads" data-testid={DOWNLOADS.section} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="overline text-aawta-magenta">Download Center</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              AAWTA Education &amp; Industry Workforce Collaborative
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Grab the flyer, the finalized agenda, the sponsorship prospectus, exhibitor and
              speaker guides, and share you're attending — everything you need in one place.
            </p>
            <div className="mt-8 rounded-2xl overflow-hidden border border-aawta-border shadow-aawta-soft">
              <img src={AAWTA.flyerUrl} alt="AAWTA Summit event flyer preview" className="w-full h-auto block" loading="lazy" />
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
            {allCards.map((item) => <DownloadCard key={item.testId} item={item} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Downloads;
